import type { Breadcrumb, ReplayFrameEvent } from '@sentry/vue';
import { sanitizeUrl } from 'pb-tracking';

import { CAPABILITY_PATH_PREFIXES } from './capabilityRoutes';

/**
 * The last gate before a URL leaves this app on a Sentry channel.
 *
 * Events, breadcrumbs, transactions and replays use the same capability-path
 * policy as edge tracking so billing and preference credentials stay private.
 *
 * A module rather than closures inside the plugin, because the plugin cannot be
 * imported by a test (it calls defineNuxtPlugin and initialises Sentry at import
 * time) and a redaction rule nothing can execute is a rule nothing can prove.
 */
const REDACT_PREFIXES = [...CAPABILITY_PATH_PREFIXES];

/**
 * sanitizeUrl parses with `new URL()` and answers null for anything that is not
 * absolute — and Sentry's navigation breadcrumbs carry RELATIVE paths. Mapping
 * that null to '[redacted]' would erase the entire "what did the user do before
 * the error" trail on every SPA navigation, so resolve a relative value against
 * a throwaway origin and strip the origin back off.
 */
const REDACT_BASE = 'https://redact.invalid';

const PRIVACY_BEE_DOMAIN = 'privacybee.com';

/**
 * A capability token reaches Sentry as span metadata too, not just as
 * `event.request.url`: an http.client span records the whole request URL in its
 * description ("GET https://…/v3/public/hives/{token}/preferences") and repeats
 * it under URL-valued data keys. Those are the keys that carry it.
 */
const URL_DATA_KEYS = ['url', 'http.url', 'http.request.url'];

/** The breadcrumb fields that hold a URL: the fetch/xhr target, and both ends of a navigation. */
const BREADCRUMB_URL_KEYS = ['url', 'from', 'to'];

/**
 * Whether a host is ours, and so whether its query string is ours to filter.
 *
 * `sanitizeUrl` drops the parameter names WE know carry secrets, and that list
 * can only ever be complete for Privacy Bee URLs. The browser talks to vendors
 * directly — SparkVault Forge takes a single-use transfer key as `?istk=` on an
 * upload URL that never passes through our API — and the next vendor will name
 * its credential something else again. So a foreign query goes whole rather
 * than filtered against a list of other people's parameter names.
 */
function isPrivacyBeeHost(hostname: string): boolean {
    if (hostname === PRIVACY_BEE_DOMAIN || hostname.endsWith(`.${PRIVACY_BEE_DOMAIN}`)) {
        return true;
    }

    // The origin this app is served from — localhost in development, a preview
    // host in CI. A same-origin URL is ours whatever the hostname happens to be.
    return typeof globalThis.location !== 'undefined' && hostname === globalThis.location.hostname;
}

function cleanUrl(raw: string): string {
    const isRelative = raw.startsWith('/');
    const sanitized = sanitizeUrl(isRelative ? REDACT_BASE + raw : raw, { redactPathPrefixes: REDACT_PREFIXES });

    if (sanitized === null) return raw.startsWith('http') ? '[redacted]' : raw;

    // A relative path is this app by construction, whatever host is serving it.
    if (isRelative) return sanitized.slice(REDACT_BASE.length);

    const url = new URL(sanitized);
    if (isPrivacyBeeHost(url.hostname)) return sanitized;

    url.search = '';
    url.hash = '';

    return url.toString();
}

/** The URL as it may leave this app, or the value unchanged when it is not one. */
export function clean(u: unknown): unknown {
    if (typeof u !== 'string' || u === '') return u;

    return cleanUrl(u);
}

/**
 * vueRouterInstrumentation copies every route param and query value onto the
 * transaction as span attributes, so `params.token` / `params.id` carry the
 * capability credential even when the URL itself has been scrubbed.
 */
export function scrubSpanData(data?: Record<string, unknown>): void {
    if (!data) return;

    for (const key of Object.keys(data)) {
        if (key.startsWith('params.') || key.startsWith('query.')) data[key] = '[redacted]';
        else if (URL_DATA_KEYS.includes(key)) data[key] = clean(data[key]);
    }
}

/**
 * Span descriptions are "<METHOD> <url>" for http spans and free text for the
 * rest, so scrub the URL half and leave the verb — and anything that is not a
 * URL is returned untouched by cleanUrl().
 */
export function cleanDescription(description?: unknown): unknown {
    if (typeof description !== 'string' || description === '') return description;

    const httpSpan = description.match(/^([A-Z]+)\s+(\S+)$/);

    return httpSpan ? `${httpSpan[1]} ${cleanUrl(httpSpan[2])}` : cleanUrl(description);
}

/** `beforeBreadcrumb`: scrubs a breadcrumb before it is recorded on the scope. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    const data = breadcrumb.data;
    if (!data) return breadcrumb;

    for (const key of BREADCRUMB_URL_KEYS) {
        if (key in data) data[key] = clean(data[key]);
    }

    return breadcrumb;
}

/** A transaction's own span or one of its children: both hold a URL in `description`. */
interface UrlBearingSpan {
    data?: Record<string, unknown>;
    /** Free text or "<METHOD> <url>"; Sentry types the trace context as an open record, so it arrives untyped. */
    description?: unknown;
}

/**
 * The parts of a Sentry event that can carry a URL.
 *
 * Structural rather than `Event`, because a Sentry `Span` is a twenty-six member
 * interface: asking for the whole type would mean nothing could exercise this
 * function without building a real transaction. Every Sentry event satisfies
 * this shape, and so does a fixture.
 */
export interface UrlBearingEvent {
    request?: { url?: string; headers?: { [key: string]: string } };
    breadcrumbs?: Breadcrumb[];
    contexts?: { trace?: UrlBearingSpan };
    spans?: UrlBearingSpan[];
}

/** `beforeSend` / `beforeSendTransaction`: scrubs every URL an event carries. */
export function scrubCapabilityUrls<T extends UrlBearingEvent>(event: T): T {
    const request = event.request;
    if (request) {
        if (request.url) request.url = cleanUrl(request.url);

        const headers = request.headers;
        if (headers && typeof headers.Referer === 'string') headers.Referer = cleanUrl(headers.Referer);
    }

    // Breadcrumbs already went through beforeBreadcrumb on their way to the
    // scope; an event assembled with its own is covered by the same rule here.
    for (const breadcrumb of event.breadcrumbs ?? []) {
        scrubBreadcrumb(breadcrumb);
    }

    if (event.contexts?.trace) {
        scrubSpanData(event.contexts.trace.data);
        event.contexts.trace.description = cleanDescription(event.contexts.trace.description);
    }

    for (const span of event.spans ?? []) {
        scrubSpanData(span.data);
        span.description = cleanDescription(span.description);
    }

    return event;
}

/**
 * `beforeAddRecordingEvent`: the replay's own channel for network activity.
 *
 * Replay does NOT reuse the SDK's fetch/xhr breadcrumbs — it drops them
 * (`handleScope` filters the `fetch` and `xhr` categories outright) and records
 * every request itself as a custom recording event tagged `performanceSpan`,
 * carrying the whole request URL in `payload.description`. That channel bypasses
 * both beforeBreadcrumb and beforeSend, so this is the only hook that sees it —
 * and it is the channel a browser→Forge upload URL, transfer key and all, would
 * otherwise leave on.
 *
 * The same tag carries `navigation.push` frames, whose `data.previous` is the
 * URL navigated away from.
 */
export function scrubRecordingEvent(event: ReplayFrameEvent): ReplayFrameEvent {
    if (event.data?.tag !== 'performanceSpan') return event;

    const payload = event.data.payload;
    payload.description = cleanUrl(payload.description);

    if (payload.op === 'navigation.push' && typeof payload.data?.previous === 'string') {
        payload.data.previous = cleanUrl(payload.data.previous);
    }

    return event;
}
